<template>
  <HomeHeaderSkeletonLoader
    v-if="loading"
    :is-admin-on="isAdminOn"
    :show-logo="projectImageUrl && !hideLogo"
  />
  <div
    v-else
    class="d-flex flex-column align-start justify-start header-container"
  >
    <div class="d-flex align-center justify-start">
      <img
        v-if="projectImageUrl && !hideLogo"
        :src="projectImageUrl"
        class="company-logo mr-4"
        alt="Company logo"
        @error="imageLoadError"
      >
      <h3 class="greeting text-left">
        {{ firstName ? `Welcome ${firstName}!` : 'Welcome!' }}
      </h3>
    </div>
    <div
      v-if="isAdminOn"
      class="d-flex flex-column align-start justify-start"
    >
      <div class="d-flex mt-4">
        <div
          v-if="nonCompliantJobPosts"
          class="da-message-icon-wrapper mr-3"
        >
          <DpplSvg
            file-name="icon-exclamation"
            :color="white"
          />
        </div>
        <div
          v-else
          class="da-success-message-icon-wrapper mr-3"
        >
          <v-icon
            class="success-icon"
            color="white"
            icon="$check"
            size="14px"
          />
        </div>
        You have&nbsp;
        <span
          @mouseenter="homeHover('Compliance')"
          @click="homeClick('Compliance')"
        >
          <router-link
            class="metric-link"
            :to="{ name: 'compliance-report' }"
          >
            <span>{{ nonCompliantJobPosts }} live, external job posts that are non-compliant</span>
          </router-link>
        </span>
      </div>
      <div class="d-flex mt-3">
        <div
          v-if="employerBrandJobPosts"
          class="da-message-icon-wrapper mr-3"
        >
          <DpplSvg
            file-name="icon-exclamation"
            :color="white"
          />
        </div>
        <div
          v-else
          class="da-success-message-icon-wrapper mr-3"
        >
          <v-icon
            class="success-icon"
            color="white"
            icon="$check"
            size="14px"
          />
        </div>
        You have&nbsp;
        <span
          @mouseenter="homeHover('Employer-Brand')"
          @click="homeClick('Employer-Brand')"
        >
          <router-link
            class="metric-link"
            :to="{ name: 'employer-brand-report' }"
          >
            <span>
              {{ employerBrandJobPosts }} live, external job posts
              that don't align with your employer branding
            </span>
          </router-link>
        </span>
      </div>
      <div class="d-flex mt-3">
        <div
          v-if="jobPerformanceJobPosts"
          class="da-message-icon-wrapper mr-3"
        >
          <DpplSvg
            file-name="icon-exclamation"
            :color="white"
          />
        </div>
        <div
          v-else
          class="da-success-message-icon-wrapper mr-3"
        >
          <v-icon
            class="success-icon"
            color="white"
            icon="$check"
            size="14px"
          />
        </div>
        You have&nbsp;
        <span
          @mouseenter="homeHover('Job-Performance')"
          @click="homeClick('Job-Performance')"
        >
          <router-link
            class="metric-link"
            :to="{ name: 'job-performance-report' }"
          >
            <span>
              {{ jobPerformanceJobPosts }} live, external job posts
              that are not optimized for external performance
            </span>
          </router-link>
        </span>
      </div>
      <div class="mt-3 text-left">
        Your organization's
        <span
          @mouseenter="homeHover('Score')"
          @click="homeClick('Score')"
        >
          <router-link
            class="metric-link"
            :to="{ name: 'published' }"
          >
            <span>average open job score</span>
          </router-link>
        </span>
        is
        <span
          class="score"
        >{{ Math.round(score || 0) }}</span>
      </div>
      <div class="mt-3 text-left">
        You have
        <span
          @mouseenter="homeHover('Users')"
          @click="homeClick('Users')"
        >
          <router-link
            class="metric-link"
            :to="{
              name: 'platform-permissions',
              query: {
                status: 'active',
              },
            }"
          >
            <span>{{ activatedUsers }} activated users</span>
          </router-link>
        </span>
      </div>
      <div
        v-if="datapeopleAnywhereEnabled && usersNotUsingDA"
        class="mt-3 text-left"
      >
        <span
          @mouseenter="homeHover('Users not using DA')"
          @click="homeClick('Users not using DA')"
        >
          <router-link
            class="metric-link"
            :to="{
              name: 'platform-permissions',
              query: {
                datapeople_anywhere: 'not_installed',
                status: 'active',
              },
            }"
          >
            <span>{{ usersNotUsingDA }} users</span>
          </router-link>
        </span>
        have not installed Datapeople Anywhere yet
      </div>
    </div>
    <div
      v-else
      class="d-flex flex-column align-start justify-start pt-1"
    >
      <div
        v-if="showInstallDAMessage"
        class="d-flex mt-3"
      >
        <div
          class="da-message-icon-wrapper mr-3"
          @mouseenter="homeHover('Install DA from Home Page')"
          @click="homeClick('Install DA from Home Page')"
        >
          <DpplSvg
            file-name="icon-exclamation"
            :color="white"
          />
        </div>
        Use Datapeople directly from your ATS.&nbsp;
        <a
          class="metric-link-anchor"
          href="https://chromewebstore.google.com/detail/datapeople-anywhere/hhnemhalmbhdldblamkefkjnalpmadhm"
        >
          Install Datapeople Anywhere
        </a>
        &nbsp;or&nbsp;
        <a
          class="metric-link-anchor"
          href="https://help.datapeople.io/article/137-how-to-install-datapeople-anywhere"
          target="_blank"
          rel="noopener noreferrer"
        >
          learn more here
        </a>
      </div>
      <div class="mt-3 text-left">
        You have
        <span
          v-if="showReqs"
          @mouseenter="homeHover('Open Reqs')"
          @click="homeClick('Open Reqs')"
        >
          <router-link
            v-if="showReqs"
            class="metric-link"
            :to="{ name: 'open-requisitions', query: { owned: 'true' } }"
          >
            <span>{{ openReqs || 0 }} open {{ openReqs === 1 ? 'req' : 'reqs' }}</span>
          </router-link>
        </span>
        {{ showReqs ? 'and' : '' }}
        <span
          @mouseenter="homeHover('Open Job Posts')"
          @click="homeClick('Open Job Posts')"
        >
          <router-link
            class="metric-link"
            :to="{ name: 'published', query: { owned: 'true' } }"
          >
            <span>{{ openPosts || 0 }} open job {{ openPosts === 1 ? 'post' : 'posts' }}</span>
          </router-link>
        </span>
      </div>
      <div class="mt-3 text-left">
        You also have
        <span
          v-if="showReqs"
          @mouseenter="homeHover('Draft Reqs')"
          @click="homeClick('Draft Reqs')"
        >
          <router-link
            class="metric-link"
            :to="{ name: 'draft-requisitions', query: { owned: 'true' } }"
          >
            <span>{{ draftReqs || 0 }} draft {{ draftReqs === 1 ? 'req' : 'reqs' }}</span>
          </router-link>
        </span>
        {{ showReqs ? 'and' : '' }}
        <span
          @mouseenter="homeHover('Draft Job Posts')"
          @click="homeClick('Draft Job Posts')"
        >
          <router-link
            class="metric-link"
            :to="{ name: 'draft', query: { owned: 'true' } }"
          >
            <span>{{ draftPosts || 0 }} draft job {{ draftPosts === 1 ? 'post' : 'posts' }}</span>
          </router-link>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useHome } from '@/stores/home/home';
import HomeHeaderSkeletonLoader from '@/components/home/HomeHeader/HomeHeaderSkeletonLoader.vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import { useTracking } from '@/stores/tracking/tracking';
import { useColors } from '@/composables/colors';

export default {
  name: 'HomeHeader',
  components: {
    HomeHeaderSkeletonLoader,
    DpplSvg,
  },
  props: {
    isAdminOn: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {
      textLight,
      basicRed,
      orange500,
      blue500,
      green500,
      white,
    } = useColors();

    return {
      textLight,
      basicRed,
      orange500,
      blue500,
      green500,
      white,
    };
  },
  data() {
    return {
      hideLogo: false,
    };
  },
  computed: {
    ...mapState(useHome, [
      'score',
      'activatedUsers',
      'usersNotUsingDA',
      'nonCompliantJobPosts',
      'employerBrandJobPosts',
      'jobPerformanceJobPosts',
      'openReqs',
      'openPosts',
      'draftReqs',
      'draftPosts',
      'userHasAtsId',
    ]),
    ...mapState(useAppAuthData, ['authData']),
    firstName() {
      return this.authData.firstName;
    },
    projectImageUrl() {
      return this.authData.projectImageUrl;
    },
    datapeopleAnywhereEnabled() {
      return this.authData.extensionJobsEnabled;
    },
    isApplicationReportingEnabled() {
      return this.authData.isApplicationReportingEnabled;
    },
    showReqs() {
      return this.isApplicationReportingEnabled && this.userHasAtsId;
    },
    showInstallDAMessage() {
      return this.datapeopleAnywhereEnabled
       && this.authData.role === 'editor' && !this.authData.hasInstalledDA;
    },
    scoreColor() {
      const scoreToUse = Math.round(this.score || 0);

      if (scoreToUse === 0) {
        return this.textLight;
      }
      if (scoreToUse > 0 && scoreToUse <= 49) {
        return this.basicRed;
      }
      if (scoreToUse >= 50 && scoreToUse <= 69) {
        return this.orange500;
      }
      if (scoreToUse >= 70 && scoreToUse <= 84) {
        return this.blue500;
      }
      if (scoreToUse >= 85) {
        return this.green500;
      }
      return this.textLight;
    },
  },
  methods: {
    ...mapActions(useTracking, ['homeHover', 'homeClick']),
    imageLoadError() {
      this.hideLogo = true;
    },
  },
};
</script>

<style lang="less" scoped>
.greeting {
  font-weight: 700;
}

.metric-link span, .metric-link-anchor {
  color: @text-brand;
  font-weight: 700;
  cursor: pointer;
  margin: 0px 2px;

  &:hover {
    background: @bg-canvas;
  }
}

.score {
  font-weight: 700;
  color: v-bind(scoreColor);
}

.company-logo {
  max-width: 48px;
  max-height: 48px;
}

.da-message-icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  background: @red-500;
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.da-success-message-icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  background: @basic-green;
  width: 20px;
  height: 20px;
  border-radius: 50%;
}
</style>
